/**
 * The two pieces of restore UI shared by every report page.
 *
 * Kept here with their own stylesheet rather than in a page's CSS, because the
 * one thing this repo reliably does with per-page report chrome is copy it
 * (FilterPopover's CSS now lives in four page stylesheets and none of the
 * profile pages). Four report pages are due to adopt this.
 *
 * Both render null when there is nothing to say, so a consumer can drop them in
 * unconditionally.
 *
 * See src/hooks/useReportRestore.js and docs/REPORT_STATE_RESTORE_PLAN.md.
 */

import './report-restore-banner.css';

const atTime = (ms) => new Date(ms)
  .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/**
 * The nav-menu offer: "you ran this before, want it back?"
 *
 * A question, not a result — nothing has run in this mount yet — so it belongs
 * above the report body and reads as a prompt rather than a warning. Declining
 * is just ignoring it and pressing Run.
 *
 * The event count is part of the offer on purpose. It makes the offer
 * self-diagnosing: the user can see at a glance whether this is the report they
 * are thinking of, rather than accepting it and then wondering why the numbers
 * look wrong.
 *
 * @param {object}   props
 * @param {object}   props.offer     `restore.offer` — null hides the banner.
 * @param {Function} props.onAccept
 * @param {Function} props.onDismiss
 * @param {boolean}  [props.show]    Gate from the page, e.g. `status === 'init'`.
 */
export function ReportRestoreOffer({ offer, onAccept, onDismiss, show = true }) {
  if (!offer || !show) return null;
  const n = offer.count;
  return (
    <div className="rr-offer">
      <span>
        {`You last ran this report at ${atTime(offer.ranAt)}`
          + ` (${n} ${n === 1 ? 'event' : 'events'}).`}
      </span>
      <button
        type="button"
        className="dp-btn dp-btn--secondary dp-btn--sm"
        onClick={onAccept}
      >
        Show that report
      </button>
      <button type="button" className="rr-offer__dismiss" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}

/**
 * The provenance note above a report that did not come fresh from the server.
 *
 * Without it a "Today" range silently presents twenty-minute-old counts as
 * current. `refreshing` is the second half of the stale-while-revalidate path:
 * the report is already on screen and a fresh query is in flight behind it.
 *
 * @param {object}        props
 * @param {number|null}   props.servedFrom  `restore.servedFrom`
 * @param {boolean}       props.refreshing  `restore.refreshing`
 */
export function ReportRestoreNote({ servedFrom, refreshing }) {
  if (!servedFrom && !refreshing) return null;
  return (
    <p className="rr-note">
      {refreshing
        ? 'Updating this report with a recent change…'
        : `Restored from your last run at ${atTime(servedFrom)}. Run to refresh.`}
    </p>
  );
}
